import { Radio, RefreshCw, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';

import { Button, Tooltip } from '@core/ui';
import { cn } from '@core/utils/cn';

import { useLiveTracking, type LiveStatus } from './LiveTrackingProvider';

/**
 * Each state says what it means, because "Live" next to a green dot is ambiguous on a
 * fleet map: it reads as a claim about the trucks rather than about the data feed. The
 * tooltip is where the distinction actually gets made.
 */
const APPEARANCE = {
  connecting: {
    label: 'Connecting',
    hint: 'Opening the live connection to the server.',
    dot: 'bg-fg-subtle',
    text: 'text-fg-muted',
  },
  live: {
    label: 'Live',
    hint: 'Connected. Positions appear here the moment the server receives them. Trackers still only report every few minutes, so a pin can be live and minutes old at the same time.',
    dot: 'bg-status-moving animate-pulse motion-reduce:animate-none',
    text: 'text-fg-muted',
  },
  reconnecting: {
    label: 'Reconnecting',
    hint: 'The live connection dropped. Retrying — positions on screen may be out of date.',
    dot: 'bg-status-stale',
    text: 'text-warning',
  },
  polling: {
    label: 'Polling',
    hint: 'The live connection failed, so positions are being fetched once a minute instead. Still accurate, just slower to arrive.',
    dot: 'bg-status-idle',
    text: 'text-accent',
  },
  paused: {
    label: 'Paused',
    hint: 'This tab is in the background, so the live connection is closed to save battery. It resumes, and refreshes, when you come back.',
    dot: 'bg-fg-subtle',
    text: 'text-fg-subtle',
  },
  offline: {
    label: 'No connection',
    hint: 'Cannot reach the server. Nothing on this screen is updating.',
    dot: 'bg-status-alert',
    text: 'text-danger',
  },
} as const satisfies Record<LiveStatus, { label: string; hint: string; dot: string; text: string }>;

/**
 * Connection state, visible at all times.
 *
 * Not decoration. A fleet map whose data has quietly stopped arriving looks exactly like
 * a fleet that has quietly stopped moving, and a dispatcher cannot tell the difference
 * from the map alone. Saying so is the difference between a stale screen and a lie.
 */
export function LiveStatusPill({ className }: { className?: string }) {
  const { status, nextRetryAt, reconnect } = useLiveTracking();
  const appearance = APPEARANCE[status];
  const countdown = useCountdown(nextRetryAt);

  const degraded = status === 'reconnecting' || status === 'polling' || status === 'offline';

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <Tooltip content={appearance.hint} side="bottom">
        <span
          // Announces only transitions INTO a degraded state. A screen-reader user gets
          // told when the data stopped being live; they do not get told "Live" every time
          // the connection blips back.
          role="status"
          aria-live="polite"
          tabIndex={0}
          aria-label={`Data connection: ${appearance.label}. ${appearance.hint}`}
          className={cn(
            'rounded-pill inline-flex items-center gap-1.5 px-2.5 py-1 text-[12px]',
            'glass ring-glass-stroke ring-1 ring-inset',
            'focus-visible:outline-accent outline-none focus-visible:outline-2 focus-visible:outline-offset-2',
            appearance.text,
          )}
        >
          <span className={cn('size-2 shrink-0 rounded-full', appearance.dot)} />
          {appearance.label}
          {countdown !== null && <span className="tnum">· {countdown}s</span>}
        </span>
      </Tooltip>

      {degraded && (
        <Button variant="ghost" size="sm" onClick={reconnect}>
          {status === 'offline' ? (
            <WifiOff className="size-3.5" />
          ) : (
            <RefreshCw className="size-3.5" />
          )}
          Retry
        </Button>
      )}
    </div>
  );
}

/** Seconds until the next attempt, or null when nothing is scheduled. */
function useCountdown(target: number | null): number | null {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (target === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(timer);
  }, [target]);

  if (target === null) return null;
  return Math.max(0, Math.ceil((target - now) / 1000));
}

export { Radio };
